import DairyService from '@src/api/services/dairy';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { StateEl } from '../style';
import { FormFields } from '@src/components/FormFields';
import { GlobalEl } from '@src/styles/app';
import DairyQuery from '@src/QueryHooks/DairyQuery';
import notify from '~/components/Notify';
import { useMutation } from 'react-query';
import Drawer from '~/components/Drawer';
import { useAuth } from '~/routes/components/AuthProvider';
import { StyledTabPane } from '~/pages/Dairy/Settings/General/style';
import { Tab } from 'semantic-ui-react';
import { StyledMenuItem } from '~/pages/Dairy/CloudStorage/style';

interface CreateEditMembershipStatusProps {
  isVisible: boolean;
  handleHiddenDrawer: () => void;
  isLoading?: boolean;
  fetchData?: () => void,
  idMembershipStatus?: any
}

const CreateEditMembershipStatuses: React.FunctionComponent<CreateEditMembershipStatusProps> = (props) => {
  const { isVisible, handleHiddenDrawer, idMembershipStatus } = props;
  const { t } = useTranslation();
  const { systemData: { languages } } = useAuth()
  const { handleSubmit, control, reset, watch, formState } = useForm({
    mode: 'onSubmit'
  });

  useEffect(() => {
    if (idMembershipStatus) {
      editMembershipStatus(idMembershipStatus);
    } else {
      const languages = getLangs()
      reset({
        status: 0,
        default: 0,
        override: 0,
        languages
      });
    }
  }, [isVisible, idMembershipStatus]);

  function getLangs() {
    return languages.map(language => ({
      id: language.id,
      name: ''
    }))
  }

  const onSuccess = () => {
    notify(t('Successfully created membership status'), 'success');
    props.fetchData()
  };

  function getParsedLanguages(statusLangs) {
    languages.map((language) => {
      const index = statusLangs.findIndex(languageText => languageText.id === language.id)
      if (index < 0) {
        statusLangs.push({
          id: language.id,
          name: '',
          order: language.order
        })
      } else {
        statusLangs[index].order = language.order
      }
    })
    statusLangs.sort((a, b) => a.order - b.order)
    statusLangs?.forEach(lang => delete lang.order)
    return statusLangs
  }

  const onMembershipStatusSuccess = (response) => {
    response.data.languages = getParsedLanguages(response.data.languages)
    reset(response.data)
  }

  const { mutateAsync: storeMembershipStatus } =
    DairyQuery.storeMembershipStatus(onSuccess);

  const { mutate: updateMembershipStatus } = useMutation(
    'membership-statuses-update',
    DairyService.updateMembershipStatus,
    {
      onSuccess: () => {
        notify(t('Successfully updated membership status'), 'success');
        props.fetchData()
      }
    }
  );

  const { isLoading: isMembershipStatusLoading, mutateAsync: editMembershipStatus } =
    DairyQuery.editMembershipStatus(onMembershipStatusSuccess);

  const onSubmit = (data) => {
    if (!idMembershipStatus) {
      storeMembershipStatus({ ...data });
    } else {
      updateMembershipStatus({ id: idMembershipStatus, data })
    }
    handleHiddenDrawer();
  };

  function errorTab() {
    //@ts-ignore
    return formState.errors?.languages?.map((lang, index) => index)?.filter(item => typeof (item) === 'number')
  }

  function getMenuItem(label, index) {
    const errors = errorTab() || []

    return <StyledMenuItem
      key={label}
      className='tab-item'
    >
      <div className={`${errors.includes(index) ? 'error' : ''}`}>{label}</div>
    </StyledMenuItem>
  }

  function renderLanguageFields() {
    return <Tab
      menu={{ secondary: true, pointing: true }}
      renderActiveOnly={false}
      panes={
        languages.map((language) => {
          const indexLanguage = languages.findIndex(languageIndex => languageIndex['id'] === language.id)
          return {
            //@ts-ignore
            menuItem: getMenuItem(language['label'], indexLanguage),
            pane: <StyledTabPane key={language.id}>
              <FormFields.Input
                control={control}
                name={`languages[${indexLanguage}].name`}
                label={t('Designation')}
                placeholder={t('Designation')}
                rules={{
                  required: {
                    value: true,
                    message: t('Please enter designation')
                  }
                }}
              />
            </StyledTabPane>
          }
        })}
    />
  }

  const renderContent = () => {
    return (
      <>
        {renderLanguageFields()}
        <StateEl.DrawerFooter>
          <GlobalEl.PrimaryButton
            disabled={Object.keys(formState.dirtyFields).length === 0}
          >{idMembershipStatus ? t('Update') : t('Add')}</GlobalEl.PrimaryButton>
        </StateEl.DrawerFooter>
      </>
    );
  };

  return <Drawer
    isVisible={isVisible}
    as
    handleSubmit={handleSubmit(onSubmit)}
    isBusy={isMembershipStatusLoading}
    header={idMembershipStatus ? t('Edit membership status') : t('Create membership status')}
    closeOnEscape
    onHide={() => {
      reset();
      handleHiddenDrawer();
    }}
    content={renderContent()}
  />
};

export default CreateEditMembershipStatuses;
